import { Box, Skeleton, styled } from '@mui/material';
import { SearchTable } from '@/components/Search/SearchTable';
import { TabPanel } from '@/components/ViewTabs';
import { useGetLabelingCoverage } from '@/hooks/api/assets/useGetLabelingCoverage';
import { useFetchAssetsWithoutLabels } from '@/hooks/api/assets/useFetchAssetsWithoutLabels';
import { useGetTenantTaxonomy } from '@/hooks/api/taxonomy/useGetTenantTaxonomy';
import { AssetsWithoutLabelsZeroState, AssetsWithoutLabelsZeroStateVariant } from './AssetsWithoutLabelsZeroState';

// The grid brings the tab's panel with it; a zero state does not, so it borrows the shared one
// rather than floating unbounded under the tab strip.
const GridRoot = styled(Box)({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
});

// Nothing to list has three different meanings, and telling them apart is the whole point: without
// a taxonomy there is nothing to label with, without assets there is nothing to label, and with
// neither missing the tenant is simply done.
const resolveZeroState = ({
  hasTaxonomy,
  totalAssets,
  labeledAssets,
}: {
  hasTaxonomy: boolean;
  totalAssets: number;
  labeledAssets: number;
}): AssetsWithoutLabelsZeroStateVariant | undefined => {
  if (!hasTaxonomy) return 'no-taxonomy';
  if (totalAssets === 0) return 'no-assets';
  if (labeledAssets >= totalAssets) return 'all-labeled';
  return undefined;
};

export const AssetsWithoutLabelsView = ({ onBuildTaxonomy }: { onBuildTaxonomy?: () => void }) => {
  const { fetchData, error } = useFetchAssetsWithoutLabels();
  const { taxonomy, isLoading: isTaxonomyLoading } = useGetTenantTaxonomy();
  const { data: coverage, isLoading: isCoverageLoading } = useGetLabelingCoverage();

  if (isTaxonomyLoading || isCoverageLoading) {
    return (
      <TabPanel>
        <Skeleton variant='rounded' height={240} />
      </TabPanel>
    );
  }

  const zeroState = resolveZeroState({
    hasTaxonomy: !!taxonomy,
    totalAssets: coverage?.totalAssets ?? 0,
    labeledAssets: coverage?.labeledAssets ?? 0,
  });

  if (zeroState) {
    return (
      <TabPanel>
        <AssetsWithoutLabelsZeroState variant={zeroState} onBuildTaxonomy={onBuildTaxonomy} />
      </TabPanel>
    );
  }

  return (
    <GridRoot>
      <SearchTable fetchMatches={fetchData} isLoading={false} error={error} shouldShowSearchTabs={false} />
    </GridRoot>
  );
};
